/**
 * Thin client for the Voice Intelligence backend. All requests route through
 * the API Gateway and authenticate with the user's Cognito ID token.
 */

import { API_CONFIG } from '../../config/apiRouting';

const BASE = API_CONFIG.gateway.baseUrl.replace(/\/$/, '');

export interface VoiceConfig {
  storeId: string;
  voicePersona?: 'eve' | 'leo' | 'rex' | 'aurora' | 'classic';
  language?: string;
  vadEnabled?: boolean;
  upsellEnabled?: boolean;
  preferredProvider?: 'auto' | 'cloud' | 'edge';
  managerPinThresholdCents?: number;
}

export interface VoiceAnalyticsDay {
  date: string;
  sessionCount: number;
  cloudCount: number;
  edgeCount: number;
  degradedCount: number;
  avgLatencyMs: number | null;
  avgDurationSeconds: number | null;
  itemsAddedTotal: number;
  toolCallTotal: number;
  errorTotal: number;
  languageCounts: Record<string, number>;
  providerCounts: Record<string, number>;
}

export interface PhoneCall {
  callId: string;
  fromNumber: string;
  toNumber: string;
  status: string;
  callOutcome: string | null;
  startedAt: string;
  transferredTo: string | null;
}

export interface VoiceFeedback {
  feedbackKey: string;
  sessionId: string | null;
  rating: number | null;
  issue: string | null;
  notes: string | null;
  submittedBy: string | null;
  submittedAt: string;
  acknowledgedBy: string | null;
  acknowledgedAt: string | null;
  resolutionNote: string | null;
}

export interface VoiceSession {
  sessionId: string;
  mode: string;
  provider: string;
  serverId: string | null;
  language: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  utteranceCount: number;
  itemsAddedCount: number;
  toolCallCount: number;
  errorCount: number;
  totalLatencyMs: number | null;
  finalOutcome: string | null;
}

type TokenGetter = () => Promise<string | null>;

async function request<T>(token: TokenGetter, path: string, init: RequestInit = {}): Promise<T> {
  const jwt = await token();
  if (!jwt) throw new Error('Not authenticated');
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${jwt}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers as Record<string, string> | undefined)
    }
  });
  const text = await res.text();
  const parsed = text ? JSON.parse(text) : {};
  if (!res.ok) {
    throw Object.assign(new Error(parsed?.error?.message || `HTTP ${res.status}`), { code: parsed?.error?.code });
  }
  return parsed.data as T;
}

export const voiceApi = {
  getConfig: (token: TokenGetter, storeId: string) =>
    request<VoiceConfig & Record<string, unknown>>(
      token, `/api/voice/config/${encodeURIComponent(storeId)}`
    ),

  updateConfig: (token: TokenGetter, storeId: string, patch: Partial<VoiceConfig>) =>
    request<VoiceConfig & Record<string, unknown>>(
      token, `/api/voice/config/${encodeURIComponent(storeId)}`,
      { method: 'PUT', body: JSON.stringify(patch) }
    ),

  rebuildContext: (token: TokenGetter, storeId: string) =>
    request<{ storeId: string; bytes: number; itemCount: number; compiledAt: string }>(
      token, `/api/voice/menu-context/${encodeURIComponent(storeId)}/rebuild`, { method: 'POST' }
    ),

  getContext: (token: TokenGetter, storeId: string) =>
    request<{ storeId: string; bytes: number; itemCount: number; compiledAt: string }>(
      token, `/api/voice/menu-context/${encodeURIComponent(storeId)}`
    ),

  getAnalytics: (token: TokenGetter, storeId: string, from?: string, to?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<{ storeId: string; from: string; to: string; days: VoiceAnalyticsDay[] }>(
      token, `/api/voice/analytics/${encodeURIComponent(storeId)}${suffix}`
    );
  },

  getEnterpriseAnalytics: (token: TokenGetter, enterpriseId: string, from?: string, to?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<VoiceAnalyticsDay>(
      token, `/api/voice/analytics/enterprise/${encodeURIComponent(enterpriseId)}${suffix}`
    );
  },

  submitFeedback: (token: TokenGetter, payload: {
    storeId: string;
    sessionId?: string;
    rating?: number;
    issue?: string;
    notes?: string;
  }) =>
    request<{ feedbackKey: string; submittedAt: string }>(
      token, `/api/voice/feedback`, { method: 'POST', body: JSON.stringify(payload) }
    ),

  listFeedback: (token: TokenGetter, storeId: string, limit?: number) => {
    const qs = limit ? `?limit=${limit}` : '';
    return request<{ storeId: string; feedback: VoiceFeedback[] }>(
      token, `/api/voice/feedback/${encodeURIComponent(storeId)}${qs}`
    );
  },

  acknowledgeFeedback: (token: TokenGetter, storeId: string, feedbackKey: string, resolutionNote?: string) =>
    request<{ feedbackKey: string; acknowledgedAt: string }>(
      token, `/api/voice/feedback/${encodeURIComponent(storeId)}/acknowledge`,
      { method: 'POST', body: JSON.stringify({ feedbackKey, resolutionNote }) }
    ),

  // voice-intel sessions (the new voice-intelligence Lambda); distinct from
  // /api/voice/sessions which is owned by the Android POS voice-ordering
  // Lambda. Keep them separate so the two domains don't step on each other.
  listSessions: (token: TokenGetter, storeId: string, limit?: number) => {
    const qs = limit ? `?limit=${limit}` : '';
    return request<{ storeId: string; sessions: VoiceSession[] }>(
      token, `/api/voice-intel/sessions/${encodeURIComponent(storeId)}${qs}`
    );
  },

  startSession: (token: TokenGetter, payload: { storeId: string; mode?: string; provider?: string; serverId?: string; language?: string }) =>
    request<{ sessionId: string; storeId: string; mode: string; startedAt: string }>(
      token, `/api/voice-intel/sessions`,
      { method: 'POST', body: JSON.stringify(payload) }
    ),

  updateSession: (token: TokenGetter, sessionId: string, patch: Record<string, unknown>) =>
    request<{ sessionId: string; updated: boolean }>(
      token, `/api/voice-intel/sessions/${encodeURIComponent(sessionId)}`,
      { method: 'PUT', body: JSON.stringify(patch) }
    ),

  endSession: (token: TokenGetter, sessionId: string, payload: { storeId: string; summary?: Record<string, unknown> }) =>
    request<{ sessionId: string; endedAt: string }>(
      token, `/api/voice-intel/sessions/${encodeURIComponent(sessionId)}/end`,
      { method: 'POST', body: JSON.stringify(payload) }
    ),

  listPhoneCalls: (token: TokenGetter, storeId: string, limit?: number) => {
    const qs = limit ? `?limit=${limit}` : '';
    return request<{ storeId: string; calls: PhoneCall[] }>(
      token, `/api/phone/calls/${encodeURIComponent(storeId)}/history${qs}`
    );
  }
};
